import type { Dispatch, PropsWithChildren, ReactElement } from 'react';
import { useReducer, createContext, useContext, useEffect } from 'react';
import predictionReducer, {
  type PredictionState,
  type PredictionStateAction,
} from './reducers/predictionReducer';
import { useQuery } from '@apollo/client';
import { useFollowing } from './FollowingProvider';
import anyInProgress from '../views/following/utils/anyInProgress';
import trackingCutOffTimeHasPassedForAllSubEvents from './utils/trackingCutOffTimeHasPassedForAllSubEvents.ts';
import { useApp } from './AppProvider';
import type { SubEvent } from '../types/events';
import {
  GET_EVENT_FOLLOWS,
  type GetEventFollowsData,
  type GetEventFollowsVariables,
} from '../views/following/graphql/getEventFollows.ts';
import removeFollowsWithoutPredictions from './utils/removeFollowsWithoutPredictions.tsx';
import { useSearchParams } from 'react-router-dom';
import useAppVisibility from './useAppVisibility.ts';

const PREDICTIONS_POLL_INTERVAL = 30000; // 30s

const defaultState: PredictionState = {
  predictions: [],
  includeGoalChasing: true,
};

type PredictionProviderCtx = [PredictionState, Dispatch<PredictionStateAction>];

const PredictionCtx = createContext<PredictionProviderCtx>([defaultState, () => null]);
const defaultSubEvents: SubEvent[] = [];

const PredictionProvider = ({ children }: PropsWithChildren): ReactElement => {
  const [state, dispatch] = useReducer(predictionReducer, defaultState);
  const [{ following }] = useFollowing();
  const [{ event }] = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAppVisible = useAppVisibility();

  const subEvents = event?.subEvents ?? defaultSubEvents;
  const queryParams = new URLSearchParams();
  queryParams.append('includePredictions', 'true');
  queryParams.append('includeGoalChasing', state.includeGoalChasing.toString());

  const queryVariables = {
    eventId: event?.id,
    queryParams: queryParams.toString(),
  };

  const { data, stopPolling, startPolling } = useQuery<
    GetEventFollowsData,
    GetEventFollowsVariables
  >(GET_EVENT_FOLLOWS, {
    fetchPolicy: 'no-cache',
    variables: queryVariables,
    skip: !following || following.length === 0 || !event?.id,
  });

  const { data: resultPredictions } = data?.getEventFollows ?? { data: [] };

  useEffect(() => {
    if (
      following.length > 0 &&
      anyInProgress(resultPredictions) &&
      !trackingCutOffTimeHasPassedForAllSubEvents(subEvents) &&
      isAppVisible
    ) {
      startPolling(PREDICTIONS_POLL_INTERVAL);
    } else {
      stopPolling();
    }
  }, [following, startPolling, stopPolling, resultPredictions, subEvents, isAppVisible]);

  useEffect(() => {
    if (data) {
      const predictions = data.getEventFollows.data;

      removeFollowsWithoutPredictions({ predictions, searchParams, setSearchParams });

      // Update state for components
      dispatch({ type: 'setPredictions', payload: predictions });
      dispatch({ type: 'setIncludeGoalChasing', payload: false });
    }
    // avoid potential feedback loop from URL params in effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return <PredictionCtx.Provider value={[state, dispatch]}>{children}</PredictionCtx.Provider>;
};

export const usePrediction = (): PredictionProviderCtx =>
  useContext<PredictionProviderCtx>(PredictionCtx);

export default PredictionProvider;
